import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  MdCalendarToday,
  MdClose,
  MdDelete,
  MdDownload,
  MdPerson,
  MdPersonAdd,
  MdRefresh,
  MdViewModule,
} from 'react-icons/md';
import { useUser } from '../context/UserContext';
import {
  consultarPanelCausas,
  consultarPanelClientesNuevos,
  consultarPanelComercialEstado,
  consultarPanelComercialKpis,
  consultarPanelOperaciones,
} from '../api/apiConfig';
import {
  fechaHoyISO,
  enrichOperations,
  MOTIVO_NUEVO_LABEL,
  normalizePanelPendientes,
  PANEL_PENDIENTES_OPTS,
  prioridadConfig,
  isDummyPayload,
} from '../utils/panelOperativo';
import { filterDailySeriesBusinessDaysChile } from '../utils/feriadosChile';
import { canAssignPanelGestionador } from '../utils/gestionCampanaAccess';
import PanelSeguimientoOperaciones, {
  SortTh,
  TableSearch,
  useTableSortSearch,
} from '../components/PanelSeguimientoOperaciones';
import './PanelComercial.css';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from "recharts";

const AUTO_REFRESH_MS = 15000;
const SORT_CLIENTES = { dateColumns: ['ultimaOperacion'] };
const managerSearchFields = (o) => [
  o.nOperacion,
  o.td,
  o.cliente,
  o.rut,
  o.ejecutivo,
  o.area,
  o.stage,
  o.gestionador,
  o.montoMM,
  o.days,
];
const newClientSearchFields = (x) => [
  x.cliente,
  x.rut,
  x.nOps,
  x.ultimaOperacion,
  x.montoMM,
  x.motivoLabel,
  x.area,
];
const deletedSearchFields = (x) => [
  x.nOperacion,
  x.cliente,
  x.ejecutivo,
  x.area,
  x.montoMM,
];

function StatusMarker({ item }) {
  const color = item.color || '#0b2b63';
  if (item.type === 'dot') {
    return <span className="pc-dot" style={{ background: color }} />;
  }
  if (item.type === 'tri') {
    return <span className="pc-tri" style={{ borderBottomColor: color }} />;
  }
  if (item.type === 'square') {
    return <span className="pc-square" style={{ background: color }} />;
  }
  if (item.type === 'x') return <span className="pc-xmark">×</span>;
  return <span className="pc-dot" style={{ background: color }} />;
}

function areaNorm(value) {
  return String(value || '')
    .replace(/[\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function BlockError({ message }) {
  if (!message) return null;
  return <div className="pc-block-error">{message}</div>;
}

function downloadXlsx(fileName, sheets) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name.slice(0, 31));
  });
  XLSX.writeFile(wb, fileName);
}

function mapDeletedExportRow(x) {
  return {
    N_OPERACION: x.nOperacion ?? '',
    CLIENTE: x.cliente || '',
    EJECUTIVO: x.ejecutivo || '',
    AREA: x.area || '',
    MONTO_MM: x.montoMM ?? '',
  };
}

const emptyErrors = () => ({
  kpis: '',
  clientes: '',
  causas: '',
  estado: '',
  operaciones: '',
});

const PanelComercial = () => {
  const { user } = useUser();
  const [fecha, setFecha] = useState(fechaHoyISO);
  const [deletedFecha, setDeletedFecha] = useState(fechaHoyISO);
  const [deletedSnapshot, setDeletedSnapshot] = useState(null);
  const [deletedLoading, setDeletedLoading] = useState(false);
  const [deletedError, setDeletedError] = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [clientes, setClientes] = useState(null);
  const [causas, setCausas] = useState(null);
  const [estado, setEstado] = useState(null);
  const [opsData, setOpsData] = useState(null);
  const [errors, setErrors] = useState(emptyErrors);
  const loadingRef = useRef(false);
  const deletedReqRef = useRef(0);

  const [loadingCeo, setLoadingCeo] = useState(false);
  const [error, setError] = useState("");
  const [datos, setDatos] = useState(null);
  const [usarSGC, setUsarSGC] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [listaPendientes, setListaPendientes] = useState([]);
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [periodo, setPeriodo] = useState("7d");
  const cacheHistorico = useRef(new Map());
  const [agrupacion, setAgrupacion] = useState("diario");
  const [modalPendientes, setModalPendientes] = useState(false);
  const [cargandoModal, setCargandoModal] = useState(false);
  const [filtroEjecutivo, setFiltroEjecutivo] = useState("");
  const [filtroGrupo, setFiltroGrupo] = useState("");
  const [filtroSucursal, setFiltroSucursal] = useState("");
  const [orden, setOrden] = useState({
    columna: null,
    direccion: "asc",
  });
  const ejecutivos = [...new Set(listaPendientes.map(o => o.Ejecutivo))]
  .filter(Boolean)
  .sort((a, b) => a.localeCompare(b, "es"));
  const gruposejecutivos = [...new Set(listaPendientes.map(o => o.GRUPO))]
  .filter(Boolean)
  .sort((a, b) => a.localeCompare(b, "es"));
  const sucursales = [...new Set(listaPendientes.map(o => o.SUCURSAL))]
  .filter(Boolean)
  .sort((a, b) => a.localeCompare(b, "es"));
  const [lineasVisibles, setLineasVisibles] = useState({
    total: true,
    giradas: true,
    pendientes: true,
    rechazadas: true,
  });

  const toggleLinea = (linea) => {
    setLineasVisibles((prev) => ({
      ...prev,
      [linea]: !prev[linea],
    }));
  };

  const load = useCallback(
    async () => {
      if (!user || loadingRef.current) return;
      loadingRef.current = true;
      setLoading(true);
      setErrors(emptyErrors());
      const fechaConsulta = fechaHoyISO();
      setFecha(fechaConsulta);
      const opts = { fecha: fechaConsulta };
      const jobs = [
        ['kpis', consultarPanelComercialKpis(user, opts), setKpis],
        ['clientes', consultarPanelClientesNuevos(user, { ...opts, vista: 'comercial' }), setClientes],
        ['causas', consultarPanelCausas(user, { ...opts, vista: 'comercial' }), setCausas],
        ['estado', consultarPanelComercialEstado(user, opts), setEstado],
        ['operaciones', consultarPanelOperaciones(user, { ...opts, vista: 'comercial' }), setOpsData],
      ];
      try {
        const results = await Promise.allSettled(jobs.map((j) => j[1]));
        const nextErrors = emptyErrors();
        results.forEach((res, i) => {
          const key = jobs[i][0];
          const setter = jobs[i][2];
          if (res.status === 'fulfilled' && res.value && res.value.ok !== false) {
            setter(res.value);
          } else {
            setter(null);
            const reason = res.status === 'rejected' ? res.reason : res.value;
            nextErrors[key] =
              reason?.message || reason?.error || `No se pudo cargar el bloque ${key}.`;
          }
        });
        setErrors(nextErrors);
      } finally {
        loadingRef.current = false;
        setLoading(false);
      }
    },
    [user],
  );

  useEffect(() => {
    load();
    const id = setInterval(() => {
      if (loadingRef.current) return;
      load();
    }, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const inicializar = async () => {
      setLoadingCeo(true);
      try {
        await cargarResumen();
        await cargarHistorico(fechaDesde, fechaHasta);
      } finally {
        setLoadingCeo(false);
      }
    };

    inicializar();

    const interval = setInterval(() => {
      cargarResumen();
    }, 5 * 60 * 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!loadingCeo) {
      cargarHistorico(fechaDesde, fechaHasta).catch(err =>
        setError(err.message)
      );
    }
  }, [fechaDesde, fechaHasta, agrupacion]);

  const prio = prioridadConfig(opsData?.config);
  const managers = kpis?.managers || [];
  const causes = causas?.causes || [];
  const status = estado?.status || [];
  const causeTotal = causes.reduce((acc, c) => acc + (Number(c.value) || 0), 0);

  const operations = useMemo(
    () =>
      enrichOperations(opsData?.operations, fecha, opsData?.config).map((o) => ({
        ...o,
        area: o.area || o.stage,
      })),
    [opsData, fecha],
  );
  const opsFila1 = useMemo(
    () => operations.filter((o) => areaNorm(o.area || o.stage) === 'en proceso fila 1'),
    [operations],
  );
  const opsFila2 = useMemo(
    () => operations.filter((o) => areaNorm(o.area || o.stage) === 'en proceso fila 2'),
    [operations],
  );
  const opsCarga1 = useMemo(
    () => operations.filter((o) => o.gestionador === 'Lissette'),
    [operations],
  );
  const opsCarga2 = useMemo(
    () => operations.filter((o) => o.gestionador === 'Javiera'),
    [operations],
  );

  const opsByPendiente = useMemo(() => {
    const map = Object.fromEntries(PANEL_PENDIENTES_OPTS.map((o) => [o.id, []]));
    for (const o of operations) {
      for (const id of normalizePanelPendientes(o.pendientes)) {
        if (map[id]) map[id].push(o);
      }
    }
    return map;
  }, [operations]);

  const pendientesDistCards = useMemo(() => {
    const totalOps = operations.length || 0;
    const clsById = {
      pendiente_pep: 'green',
      pendiente_uaf: 'teal',
      verificacion: 'blue',
      cesion: 'purple',
      aceptacion: 'red',
      carta_guia: 'orange',
      cheque: 'navy',
      aprobacion: 'blue',
    };
    return PANEL_PENDIENTES_OPTS.map((opt) => {
      const rows = opsByPendiente[opt.id] || [];
      const count = rows.length;
      const pct = totalOps ? Math.round((count / totalOps) * 1000) / 10 : 0;
      return {
        key: `pend-${opt.id}`,
        id: opt.id,
        label: opt.label.toUpperCase(),
        value: count,
        sub: totalOps ? `${pct}% de ops pendientes`.replace('.', ',') : 'operaciones',
        cls: clsById[opt.id] || '',
        action: `pend:${opt.id}`,
      };
    });
  }, [opsByPendiente, operations.length]);

  const newClientRows = useMemo(
    () =>
      (clientes?.details || []).map((x) => ({
        ...x,
        nOps: x.nOps ?? x.n_ops,
        ultimaOperacion: x.ultimaOperacion || x.ultima_operacion,
        motivoLabel: MOTIVO_NUEVO_LABEL[x.motivo] || x.motivo || '',
        area: x.area || x.stage,
      })),
    [clientes],
  );
  const deletedRows = deletedSnapshot?.details || [];

  const modalRowsRaw = useMemo(() => {
    if (modal === 'g1') return opsFila1;
    if (modal === 'g2') return opsFila2;
    if (modal === 'carga1') return opsCarga1;
    if (modal === 'carga2') return opsCarga2;
    if (typeof modal === 'string' && modal.startsWith('pend:')) {
      return opsByPendiente[modal.slice(5)] || [];
    }
    if (modal === 'newClients') return newClientRows;
    if (modal === 'deleted') return deletedRows;
    return [];
  }, [
    modal,
    opsFila1,
    opsFila2,
    opsCarga1,
    opsCarga2,
    opsByPendiente,
    newClientRows,
    deletedRows,
  ]);

  const modalFieldsGetter =
    modal === 'newClients'
      ? newClientSearchFields
      : modal === 'deleted'
        ? deletedSearchFields
        : managerSearchFields;
  const modalSortOptions = modal === 'newClients' ? SORT_CLIENTES : undefined;
  const modalTable = useTableSortSearch(modalRowsRaw, modalFieldsGetter, modalSortOptions);

  useEffect(() => {
    modalTable.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal, deletedFecha]);

  const loadDeleted = useCallback(
    async (fechaConsulta) => {
      if (!user) return;
      const req = ++deletedReqRef.current;
      setDeletedLoading(true);
      setDeletedError('');
      try {
        const result = await consultarPanelComercialKpis(user, { fecha: fechaConsulta });
        if (req !== deletedReqRef.current) return;
        if (result && result.ok !== false) {
          setDeletedSnapshot(result.deletedToday || { count: 0, details: [] });
        } else {
          setDeletedSnapshot({ count: 0, details: [] });
          setDeletedError(result?.message || result?.error || 'No se pudo cargar las operaciones eliminadas.');
        }
      } catch (err) {
        if (req !== deletedReqRef.current) return;
        setDeletedSnapshot({ count: 0, details: [] });
        setDeletedError(err?.message || 'No se pudo cargar las operaciones eliminadas.');
      } finally {
        if (req === deletedReqRef.current) setDeletedLoading(false);
      }
    },
    [user],
  );

  useEffect(() => {
    if (modal !== 'deleted') return;
    loadDeleted(deletedFecha);
  }, [modal, deletedFecha, loadDeleted]);

  const closeModal = () => {
    setModal(null);
  };
  const canAssign = canAssignPanelGestionador(user);

  const patchOperation = (nOperacion, fields) => {
    const key = String(nOperacion);
    setOpsData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        operations: (prev.operations || []).map((o) =>
          String(o.nOperacion ?? '') === key ? { ...o, ...fields } : o,
        ),
      };
    });
  };

  const exportDeletedExcel = () => {
    downloadXlsx(`panel_comercial_eliminados_${deletedFecha}.xlsx`, [
      { name: 'Eliminados', rows: modalTable.rows.map(mapDeletedExportRow) },
    ]);
  };

  const kpiCards = [
    {
      key: 'pending',
      icon: <MdViewModule />,
      cls: '',
      label: 'OPERACIONES PENDIENTES',
      value: kpis?.operationsPending ?? '—',
      sub: 'operaciones',
    },
    {
      key: 'newClients',
      icon: <MdPersonAdd />,
      cls: 'green',
      label: 'CLIENTES NUEVOS',
      value: clientes?.count ?? '—',
      sub: 'clientes',
      action: 'newClients',
    },
    {
      key: 'g1',
      icon: <MdPerson />,
      cls: 'blue',
      label: 'FILA 1',
      value: managers[0]?.operations ?? '—',
      sub: 'operaciones',
      action: 'g1',
    },
    {
      key: 'g2',
      icon: <MdPerson />,
      cls: 'purple',
      label: 'FILA 2',
      value: managers[1]?.operations ?? '—',
      sub: 'operaciones',
      action: 'g2',
    },
    ...(canAssign
      ? [
          {
            key: 'carga-lissette',
            icon: <MdPerson />,
            cls: 'blue',
            label: 'LISSETTE',
            value: opsCarga1.length,
            sub: 'ops asignadas',
            action: 'carga1',
          },
          {
            key: 'carga-javiera',
            icon: <MdPerson />,
            cls: 'purple',
            label: 'JAVIERA',
            value: opsCarga2.length,
            sub: 'ops asignadas',
            action: 'carga2',
          },
        ]
      : []),
    {
      key: 'deleted',
      icon: <MdDelete />,
      cls: 'red',
      label: 'OPERACIONES ELIMINADAS (HOY)',
      value: kpis?.deletedToday?.count ?? '—',
      sub: 'operaciones',
      action: 'deleted',
    },
  ];

  const openDetail = (kind) => {
    if (kind === 'newClients') setModal('newClients');
    if (kind === 'deleted') {
      const hoy = fechaHoyISO();
      setDeletedFecha(hoy);
      setDeletedError('');
      setDeletedSnapshot(kpis?.deletedToday || { count: 0, details: [] });
      setModal('deleted');
    }
    if (kind === 'g1' || kind === 'g2' || kind === 'carga1' || kind === 'carga2') setModal(kind);
    if (typeof kind === 'string' && kind.startsWith('pend:')) setModal(kind);
  };

  const modalPendienteLabel = useMemo(() => {
    if (typeof modal !== 'string' || !modal.startsWith('pend:')) return '';
    const id = modal.slice(5);
    return PANEL_PENDIENTES_OPTS.find((o) => o.id === id)?.label || id;
  }, [modal]);

  const isOpsDetailModal =
    modal === 'g1' ||
    modal === 'g2' ||
    modal === 'carga1' ||
    modal === 'carga2' ||
    (typeof modal === 'string' && modal.startsWith('pend:'));

  const cambiarPeriodo = (nuevoPeriodo) => {
    const hoy = new Date();
    const desde = new Date(hoy);

    switch (nuevoPeriodo) {
      case "7d":
        desde.setDate(hoy.getDate() - 7);
        break;
      case "15d":
        desde.setDate(hoy.getDate() - 15);
        break;
      case "1m":
        desde.setMonth(hoy.getMonth() - 1);
        break;
      default:
        break;
    }

    if (nuevoPeriodo !== "custom") {
      setFechaDesde(desde.toISOString().slice(0, 10));
      setFechaHasta(hoy.toISOString().slice(0, 10));
    }

    setPeriodo(nuevoPeriodo);
  };

  const cargarHistorico = async (desde, hasta) => {
    const key = `${desde || "default"}_${hasta || "default"}_${agrupacion}`;
    const ahora = Date.now();

    const cached = cacheHistorico.current.get(key);

    if (cached && ahora < cached.expira) {
      setHistorico(cached.data);
      return;
    }

    const res = await fetch('https://5xcjudvrkh.execute-api.us-east-1.amazonaws.com/DEV/historico', {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        fecha_desde: desde,
        fecha_hasta: hasta,
        agrupacion,
      }),
    });

    if (!res.ok) {
      throw new Error("No fue posible obtener los datos históricos");
    }

    const json = await res.json();

    cacheHistorico.current.set(key, {
      data: json,
      expira: Date.now() + msHastaMedianoche(),
    });

    setHistorico(json);
  };

  const cargarResumen = async () => {
    try {
      const res = await fetch('https://5xcjudvrkh.execute-api.us-east-1.amazonaws.com/DEV/data', {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) throw new Error("No fue posible obtener los datos");

      const json = await res.json();
      setDatos(json);
    } catch (err) {
      setError(err.message);
    }
  };

  const msHastaMedianoche = () => {
    const ahora = new Date();
    const manana = new Date(ahora);
    manana.setDate(ahora.getDate() + 1);
    manana.setHours(0, 0, 0, 0);
    return manana.getTime() - ahora.getTime();
  };

  const openModalPendientes = async (tipo) => {
    try {

      setModalPendientes(true);
      setCargandoModal(true);

      const res = await fetch(`https://5xcjudvrkh.execute-api.us-east-1.amazonaws.com/DEV/pendientes`, {
        method: "POST",
        body: JSON.stringify({ tipo : tipo }),
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) throw new Error("No fue posible obtener los datos");

      const json = await res.json();
      setListaPendientes(json);

    } catch (err) {
      setError(err.message);
      console.log(err.message);
    } finally {
      setCargandoModal(false);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return "--";

    const [anio, mes, dia] = fecha.split("-");
    return `${dia}/${mes}/${anio}`;
  };

  const formatHora = (hora) => {
    if (!hora) return "--";
    return hora.slice(0, 5);
  };

  const formatMonto = (monto) => {
    if (monto == null) return "--";
    return new Intl.NumberFormat("es-CL", {
      style: "currency",
      currency: "CLP",
      maximumFractionDigits: 0,
    }).format(monto);
  };

  const getClaseAlerta = (fecha, hora, area) => {
    if (!fecha || !hora) return "";
    if (area === "Depositada" || area === "Tesoreria") return "";

    const [anio, mes, dia] = fecha.split("-").map(Number);
    const [h, m] = hora.slice(0, 5).split(":").map(Number);

    const inicio = new Date(anio, mes - 1, dia, h, m);
    const hoy = new Date();

    inicio.setHours(0, 0, 0, 0);
    hoy.setHours(0, 0, 0, 0);

    let diasHabiles = 0;
    const fechaActual = new Date(inicio);

    while (fechaActual < hoy) {
      fechaActual.setDate(fechaActual.getDate() + 1);

      const diaSemana = fechaActual.getDay();

      if (diaSemana !== 0 && diaSemana !== 6) {
        diasHabiles++;
      }
    }

    if (diasHabiles >= 4) return "alerta-4";
    if (diasHabiles >= 2) return "alerta-2";

    return "";
  };

  const renderIcono = (icono) => {
    if (!icono) return null;

    if (icono === "no") {
      return (
        <svg width="18" height="18" viewBox="0 0 18 18">
          <line
            x1="4"
            y1="4"
            x2="14"
            y2="14"
            stroke="#8B0000"
            strokeWidth="3.5"
            strokeLinecap="round"
          />
          <line
            x1="14"
            y1="4"
            x2="4"
            y2="14"
            stroke="#DA291C"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      );
    }

    const [figura, color] = icono.split("_");

    const colores = {
      rojo: "#DA291C",
      amarillo: "#EEEE8F",
      verde: "#3B9C6B",
      celeste: "#6EC6E8",
      negra: "#2B2B2B",
      cafe: "#837F53",
      azul: "#3232FA",
      gris: "#ADADAD",
      violeta: "#E096F7"
    };

    const base = colores[color] || "#64748B";
    const id = `grad-${figura}-${color}`;

    return (
      <svg width="18" height="18" viewBox="0 0 18 18">
        <defs>
          <radialGradient id={id} cx="35%" cy="30%" r="75%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
            <stop offset="35%" stopColor={base} />
            <stop offset="100%" stopColor="#1a1a1a" />
          </radialGradient>
        </defs>

        {figura === "circulos" && (
          <circle
            cx="9"
            cy="9"
            r="7"
            fill={`url(#${id})`}
            stroke="#1c1c1c"
            strokeWidth="1.2"
          />
        )}

        {figura === "cuadrados" && (
          <rect
            x="2"
            y="2"
            width="14"
            height="14"
            fill={`url(#${id})`}
            stroke="#1c1c1c"
            strokeWidth="1.2"
          />
        )}

        {figura === "triangulo" && (
          <polygon
            points="9,2 16,15 2,15"
            fill={`url(#${id})`}
            stroke="#1c1c1c"
            strokeWidth="1.2"
            strokeLinejoin="round"
          />
        )}
      </svg>
    );
  };

  const cambiarOrden = (columna) => {
    setOrden((prev) => ({
      columna,
      direccion:
        prev.columna === columna && prev.direccion === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const listaPendientesFiltrada = listaPendientes.filter((e) =>
    !filtroEjecutivo || e.Ejecutivo === filtroEjecutivo
  ).filter((g) => 
    !filtroGrupo || g.GRUPO === filtroGrupo
  ).filter((s) =>
    !filtroSucursal || s.SUCURSAL === filtroSucursal
  );

  const listaPendientesOrdenada = [...listaPendientesFiltrada].sort((a, b) => {
    if (!orden.columna) return 0;

    let valorA;
    let valorB;

    switch (orden.columna) {
      case "Area":
        valorA = Number(a.Id) || 0;
        valorB = Number(b.Id) || 0;
        break;

      case "Monto":
        valorA = Number(a.Monto) || 0;
        valorB = Number(b.Monto) || 0;
        break;

      case "Operacion":
        valorA = Number(a.N_Operacion) || 0;
        valorB = Number(b.N_Operacion) || 0;
        break;

      case "Fecha":
        valorA = new Date(`${a.Fecha}T00:00:00`).getTime();
        valorB = new Date(`${b.Fecha}T00:00:00`).getTime();
        break;

      case "Hora_aprobH":
        valorA = a.Hora_aprobH || "00:00";
        valorB = b.Hora_aprobH || "00:00";
        break;

      case "Hora_aprobE":
        valorA = a.Hora_aprobE || "00:00";
        valorB = b.Hora_aprobE || "00:00";
        break;

      case "Cliente":
        valorA = a.Cliente.toLowerCase() || "";
        valorB = b.Cliente.toLowerCase() || "";
        break;

      case "Doc":
        valorA = a.TD.toLowerCase() || "";
        valorB = b.TD.toLowerCase() || "";
        break;

      case "Grupo":
        valorA = a.GRUPO || "";
        valorB = b.GRUPO || "";
        break;

      case "Sucursal":
        valorA = a.SUCURSAL || "";
        valorB = b.SUCURSAL || "";
        break;

      case "Ejecutivo":
        valorA = a.Ejecutivo.toLowerCase() || "";
        valorB = b.Ejecutivo.toLowerCase() || "";
        break;

      default:
        valorA = a[orden.columna];
        valorB = b[orden.columna];
    }

    if (valorA < valorB) return orden.direccion === "asc" ? -1 : 1;
    if (valorA > valorB) return orden.direccion === "asc" ? 1 : -1;
    if (typeof valorA === "string" || typeof valorB === "string") {
      const resultado = (valorA || "").localeCompare(valorB || "", "es", {
        sensitivity: "base",
      });
      return orden.direccion === "asc" ? resultado : -resultado;
    }

    if (valorA < valorB) return orden.direccion === "asc" ? -1 : 1;
    if (valorA > valorB) return orden.direccion === "asc" ? 1 : -1;
    return 0;
  });

  const fechas_excluidas = [
    "2026-05-03",
    "2026-05-21",
    "2026-07-11",
    "2026-07-12",
    "2026-07-19",
    "2026-08-01",
    "2026-09-11",
    "2026-09-18",
  ];

  const total = usarSGC ? (datos?.total_sgc ?? 0) : (datos?.total ?? 0);
  const giradas = usarSGC ? (datos?.giradas_sgc ?? 0) : (datos?.giradas ?? 0);
  const pendientes = usarSGC ? (datos?.pendientes_sgc ?? 0) : (datos?.pendientes ?? 0);
  const rechazadas = usarSGC ? (datos?.rechazadas_sgc ?? 0) : (datos?.rechazadas ?? 0);

  const porcentaje = (valor) =>
    total ? ((valor / total) * 100).toFixed(1) : "0.0";

  const historicoBase =
    agrupacion === "diario"
      ? filterDailySeriesBusinessDaysChile(
          (historico || []).filter((e) => !fechas_excluidas.includes(e.fecha)),
        )
      : (historico || []).filter((e) => !fechas_excluidas.includes(e.fecha));

  const datosGrafico = historicoBase.map((d) => ({
    fecha: agrupacion === "diario"
      ? d.fecha.slice(5)
      : `Sem ${d.fecha.slice(5)}`,
    rechazadas: usarSGC ? d.rechazadas_sgc : d.rechazadas,
    pendientes: usarSGC ? d.pendientes_sgc : d.pendientes,
    giradas: usarSGC ? d.giradas_sgc : d.giradas,
    total: usarSGC ? d.total_sgc : d.total
  }));

  return (
    <div className="pc-dash">
      <header className="pc-topbar">
        <div>
          <h1>Panel Comercial</h1>
          <p>Resumen diario de estado de clientes y gestiones</p>
        </div>
        <div className="pc-toolbar">
          <button
            type="button"
            className="pc-btn"
            onClick={() => load()}
            disabled={loading}
          >
            <MdRefresh />
            {loading ? 'CARGANDO…' : 'ACTUALIZAR'}
          </button>
        </div>
      </header>

      <section className={`pc-kpi-grid${canAssign ? ' pc-kpi-grid-wide' : ''}`}>
        {kpiCards.map((c) => (
          <article
            key={c.key}
            className={`pc-kpi-card${c.action ? ' clickable' : ''}`}
            onClick={c.action ? () => openDetail(c.action) : undefined}
            onDoubleClick={c.action ? () => openDetail(c.action) : undefined}
            onKeyDown={
              c.action
                ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openDetail(c.action);
                    }
                  }
                : undefined
            }
            role={c.action ? 'button' : undefined}
            tabIndex={c.action ? 0 : undefined}
          >
            <div className={`pc-kpi-icon ${c.cls}`}>{c.icon}</div>
            <div>
              <div className="pc-kpi-label">{c.label}</div>
              <div className="pc-kpi-value">{c.value}</div>
              <div className="pc-kpi-sub">{c.sub}</div>
            </div>
            {c.action ? <div className="pc-kpi-link">Ver detalle →</div> : null}
          </article>
        ))}
      </section>
      <BlockError message={errors.kpis || errors.clientes} />

      <section className="pc-pendientes-dist">
        <div className="pc-pendientes-dist-head">
          <div className="pc-card-title">DISTRIBUCIÓN DE PENDIENTES MARCADOS</div>
        </div>
        <div className="pc-pendientes-dist-grid">
          {pendientesDistCards.map((c) => (
            <article
              key={c.key}
              className="pc-kpi-card clickable"
              onClick={() => openDetail(c.action)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openDetail(c.action);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className={`pc-kpi-icon ${c.cls}`}>
                <MdViewModule />
              </div>
              <div>
                <div className="pc-kpi-label">{c.label}</div>
                <div className="pc-kpi-value">{c.value}</div>
                <div className="pc-kpi-sub">{c.sub}</div>
              </div>
              <div className="pc-kpi-link">Ver detalle →</div>
            </article>
          ))}
        </div>
      </section>

      <section className="pc-middle-grid">
        <article className="pc-card">
          <div className="pc-card-title">
            PRINCIPALES CAUSAS DE DETENCIÓN (DEL DÍA)
            {isDummyPayload(causas) ? <span className="panel-dummy-badge">(Datos dummy)</span> : null}
          </div>
          <BlockError message={errors.causas} />
          {causes.length === 0 ? (
            <div className="pc-empty-state">No hay detenciones (cuadrados) en esta fecha.</div>
          ) : (
            causes.map((c) => (
            <div key={c.label} className="pc-cause-row">
              <div>{c.label}</div>
              <div className="pc-track">
                <div
                  className="pc-fill"
                  style={{
                    width: `${Math.min(100, Number(c.pct) || 0)}%`,
                    background: c.color,
                  }}
                />
              </div>
              <strong>{c.value}</strong>
              <span>({c.pct}%)</span>
            </div>
            ))
          )}
          <div className="pc-foot-total">Total: {causeTotal} operaciones</div>
        </article>

        <article className="pc-card">
          <div className="pc-card-title">ESTADO GENERAL DE OPERACIONES</div>
          <BlockError message={errors.estado} />
          <div className="pc-status-grid">
            {status.map((s, idx) =>
              s.type === 'blank' ? (
                <div key={`blank-${idx}`} />
              ) : (
                <div key={`${s.label}-${idx}`} className="pc-status-item">
                  <StatusMarker item={s} />
                  <span>{s.label}</span>
                  <strong>{s.value}</strong>
                </div>
              ),
            )}
          </div>
        </article>
      </section>
      
      <section className="pc-middle-grid">
        <article className="pc-card">
          <div className='space-between'>
            <div className="pc-card-title">
              ESTADO CEO ACTUAL
            </div>
            <label className="toggle-container">
              <input
                type="checkbox"
                checked={usarSGC}
                onChange={(e) => setUsarSGC(e.target.checked)}
              />
              <span className="toggle-slider no-print"></span>
              <span className="toggle-text no-print">Excluir Sin Giro</span>
            </label>
          </div>
          <div className='space'></div>
          <div className="dashboard-grid">
            <div className="metric-card total">
              <span className="metric-label">Total</span>
              {loadingCeo ? (<h1>Cargando..</h1>) : (<h1>{total}</h1>)}
            </div>

            <div className="metric-card success clickable" onClick={() => openModalPendientes('Giradas')}>
              <span className="metric-label">Giradas</span>
              {loadingCeo ? (<h1>Cargando..</h1>)
              : (
                <div className='metric-data'>
                  <h2>{giradas}</h2>
                  <span>{porcentaje(giradas)}%</span>
                </div>
              )}
            </div>

            <div className="metric-card warning clickable" onClick={() => openModalPendientes('Pendientes')}>
              <span className="metric-label">Pendientes</span>
              {loadingCeo ? (<h1>Cargando..</h1>)
              : (
                <div className='metric-data'>
                  <h2>{pendientes}</h2>
                  <span>{porcentaje(pendientes)}%</span>
                </div>
              )}
            </div>

            <div className="metric-card danger clickable" onClick={() => openModalPendientes('Rechazadas')}>
              <span className="metric-label">Rechazadas</span>
              {loadingCeo ? (<h1>Cargando..</h1>)
              : (
                <div className='metric-data'>
                  <h2>{rechazadas}</h2>
                  <span>{porcentaje(rechazadas)}%</span>
                </div>
              )}
            </div>
          </div>
        </article>

        <article className="pc-card">
          <div className="pc-card-title">EVOLUCIÓN CEO FINAL DEL DÍA</div>
          <div className='justify-end'>
            {periodo === "custom" ? (
              <div className="custom-range">
                <input
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                />
                <span>—</span>
                <input
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                />
              </div>
            ) : (<div className='placeholder'></div>)
            }
            <div className="modal-header-actions">
              <select
                className="chart-filter"
                value={periodo}
                onChange={(e) => cambiarPeriodo(e.target.value)}
              >
                <option value="7d">1 Semana</option>
                <option value="15d">15 Días</option>
                <option value="1m">1 Mes</option>
                <option value="custom">Personalizado</option>
              </select>
              <select
                className="chart-filter"
                value={agrupacion}
                onChange={(e) => setAgrupacion(e.target.value)}
              >
                <option value="diario">Diario</option>
                <option value="semanal">Semanal</option>
              </select>
            </div>
          </div>
          <div className="line-toggle-group">
            <label className='total'>
              <input
                type="checkbox"
                checked={lineasVisibles.total}
                onChange={() => toggleLinea("total")}
              />
              Total
            </label>

            <label className='giradas'>
              <input
                type="checkbox"
                checked={lineasVisibles.giradas}
                onChange={() => toggleLinea("giradas")}
              />
              Giradas
            </label>

            <label className='pendientes'>
              <input
                type="checkbox"
                checked={lineasVisibles.pendientes}
                onChange={() => toggleLinea("pendientes")}
              />
              Pendientes
            </label>

            <label className='rechazadas'>
              <input
                type="checkbox"
                checked={lineasVisibles.rechazadas}
                onChange={() => toggleLinea("rechazadas")}
              />
              Rechazadas
            </label>
          </div>
          <ResponsiveContainer className="chart" height="300">
            <LineChart data={datosGrafico}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
              <XAxis dataKey="fecha"/>
              <YAxis/>
              <Tooltip/>
              <Legend/>

              {lineasVisibles.giradas && (
                <Line
                  type="monotone"
                  dataKey="giradas"
                  name="Giradas"
                  stroke="#10B981"
                  strokeWidth={3}
                  dot={false}
                />
                )}

                {lineasVisibles.pendientes && (
                <Line
                  type="monotone"
                  dataKey="pendientes"
                  name="Pendientes"
                  stroke="#F59E0B"
                  strokeWidth={3}
                  dot={false}
                />
                )}

                {lineasVisibles.rechazadas && (
                <Line
                  type="monotone"
                  dataKey="rechazadas"
                  name="Rechazadas"
                  stroke="#DA291C"
                  strokeWidth={3}
                  dot={false}
                />
                )}

                {lineasVisibles.total && (
                <Line
                  type="monotone"
                  dataKey="total"
                  name="Total"
                  stroke="#425563"
                  strokeWidth={3}
                  dot={false}
                />
                )}
            </LineChart>
          </ResponsiveContainer>
        </article>
      </section>

      <PanelSeguimientoOperaciones
        operations={operations}
        loading={loading}
        error={errors.operaciones}
        fecha={fecha}
        user={user}
        exportFileName={`panel_comercial_${fecha}.xlsx`}
        onPatch={patchOperation}
        prioridad={prio}
        columnWidthsKey="mgc.panelComercial.opsColWidths.v1"
      />

      {isOpsDetailModal ? (
        <div
          className="pc-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="pc-modal">
            <div className="pc-modal-header">
              <h2>
                {modal === 'g1'
                  ? 'Fila 1: operaciones en gestión'
                  : modal === 'g2'
                    ? 'Fila 2: operaciones en gestión'
                    : modal === 'carga1'
                      ? 'Lissette: operaciones asignadas'
                      : modal === 'carga2'
                        ? 'Javiera: operaciones asignadas'
                        : `Pendientes · ${modalPendienteLabel} (${modalRowsRaw.length})`}
              </h2>
              <button type="button" className="pc-modal-close" onClick={closeModal}>
                <MdClose />
              </button>
            </div>
            <div className="pc-modal-toolbar">
              <TableSearch
                value={modalTable.search}
                onChange={modalTable.setSearch}
                placeholder="Buscar en todas las columnas..."
              />
            </div>
            <table className="pc-detail-table">
              <thead>
                <tr>
                  <SortTh column="nOperacion" label="N° operación" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="td" label="TD" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="cliente" label="Cliente" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="rut" label="RUT" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="ejecutivo" label="Ejecutivo" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="area" label="Área" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="gestionador" label="Gestionador" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="montoMM" label="Monto" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="days" label="Días en espera" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                </tr>
              </thead>
              <tbody>
                {modalTable.rows.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="pc-empty-state">
                      {modalTable.total === 0
                        ? modal === 'carga1' || modal === 'carga2'
                          ? 'No hay operaciones asignadas a esta gestionadora.'
                          : typeof modal === 'string' && modal.startsWith('pend:')
                            ? 'Sin operaciones con este pendiente marcado.'
                            : 'No hay operaciones en esta fila.'
                        : 'No hay resultados.'}
                    </td>
                  </tr>
                ) : (
                  modalTable.rows.map((o, idx) => (
                    <tr key={o.id ?? `${o.nOperacion || 'op'}-${idx}`}>
                      <td>{o.nOperacion || '—'}</td>
                      <td>{o.td || '—'}</td>
                      <td>{o.cliente || '—'}</td>
                      <td>{o.rut || '—'}</td>
                      <td>{o.ejecutivo || '—'}</td>
                      <td>{o.area || o.stage || '—'}</td>
                      <td>{o.gestionador || '—'}</td>
                      <td>$ {o.montoMM} MM</td>
                      <td>{o.days}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {modal === 'newClients' ? (
        <div
          className="pc-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="pc-modal">
            <div className="pc-modal-header">
              <h2>Clientes nuevos con operaciones pendientes</h2>
              <button type="button" className="pc-modal-close" onClick={closeModal}>
                <MdClose />
              </button>
            </div>
            <div className="pc-modal-toolbar">
              <TableSearch
                value={modalTable.search}
                onChange={modalTable.setSearch}
                placeholder="Buscar en todas las columnas..."
              />
            </div>
            <table className="pc-detail-table">
              <thead>
                <tr>
                  <SortTh column="cliente" label="Cliente" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="rut" label="RUT" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="nOps" label="Ops. históricas" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="ultimaOperacion" label="Última operación" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="montoMM" label="Monto pendiente" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="motivoLabel" label="Motivo" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="area" label="Área" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                </tr>
              </thead>
              <tbody>
                {modalTable.rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="pc-empty-state">
                      {modalTable.total === 0
                        ? 'No hay clientes nuevos en esta fecha.'
                        : 'No hay resultados.'}
                    </td>
                  </tr>
                ) : (
                  modalTable.rows.map((x, idx) => (
                    <tr key={`${x.cliente}-${x.rut || idx}`}>
                      <td>{x.cliente}</td>
                      <td>{x.rut || '—'}</td>
                      <td>{x.nOps ?? '—'}</td>
                      <td>{x.ultimaOperacion || '—'}</td>
                      <td>$ {x.montoMM} MM</td>
                      <td>{x.motivoLabel || '—'}</td>
                      <td>{x.area || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {modal === 'deleted' ? (
        <div
          className="pc-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="pc-modal">
            <div className="pc-modal-header">
              <h2>Operaciones eliminadas</h2>
              <button type="button" className="pc-modal-close" onClick={closeModal}>
                <MdClose />
              </button>
            </div>
            <div className="pc-modal-toolbar">
              <label className="pc-date-box pc-date-box-compact">
                <MdCalendarToday />
                <input
                  type="date"
                  max={fechaHoyISO()}
                  value={deletedFecha}
                  onChange={(e) => {
                    if (e.target.value) setDeletedFecha(e.target.value);
                  }}
                />
              </label>
              <TableSearch
                value={modalTable.search}
                onChange={modalTable.setSearch}
                placeholder="Buscar en todas las columnas..."
              />
              <button type="button" className="pc-btn small" onClick={exportDeletedExcel}>
                <MdDownload />
                EXPORTAR
              </button>
            </div>
            <BlockError message={deletedError} />
            <table className="pc-detail-table">
              <thead>
                <tr>
                  <SortTh column="nOperacion" label="N° operación" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="cliente" label="Cliente" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="ejecutivo" label="Ejecutivo" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="area" label="Área" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                  <SortTh column="montoMM" label="Monto" sortColumn={modalTable.sortColumn} sortDirection={modalTable.sortDirection} onSort={modalTable.toggleSort} />
                </tr>
              </thead>
              <tbody>
                {modalTable.rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="pc-empty-state">
                      {deletedLoading
                        ? 'Cargando…'
                        : modalTable.total === 0
                          ? 'No hay operaciones eliminadas para esta fecha (si es un día anterior, no hubo snapshot guardado).'
                          : 'No hay resultados.'}
                    </td>
                  </tr>
                ) : (
                  modalTable.rows.map((x, idx) => (
                    <tr key={`${x.nOperacion || x.cliente}-${idx}`}>
                      <td>{x.nOperacion || '—'}</td>
                      <td>{x.cliente || '—'}</td>
                      <td>{x.ejecutivo || '—'}</td>
                      <td>{x.area || '—'}</td>
                      <td>$ {x.montoMM} MM</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {modalPendientes && (
        <div className="modal-overlay" onClick={() => setModalPendientes(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Operaciones Pendientes</h3>

              <div className="modal-header-actions">
                <select
                  className="ejecutivo-filter"
                  value={filtroEjecutivo}
                  onChange={(e) => setFiltroEjecutivo(e.target.value)}
                >
                  <option value="">Todos los ejecutivos</option>
                  {ejecutivos.map((ejecutivo) => (
                    <option key={ejecutivo} value={ejecutivo}>
                      {ejecutivo}
                    </option>
                  ))}
                </select>
                

                <select
                  className="ejecutivo-filter"
                  value={filtroGrupo}
                  onChange={(e) => setFiltroGrupo(e.target.value)}
                >
                  <option value="">Todos los grupos</option>
                  {gruposejecutivos.map((grupo) => (
                    <option key={grupo} value={grupo}>
                      {grupo}
                    </option>
                  ))}
                </select>

                <select
                  className="ejecutivo-filter"
                  value={filtroSucursal}
                  onChange={(e) => setFiltroSucursal(e.target.value)}
                >
                  <option value="">Todas las sucursales</option>
                  {sucursales.map((sucursal) => (
                    <option key={sucursal} value={sucursal}>
                      {sucursal}
                    </option>
                  ))}
                </select>

                <button
                  className="modal-close"
                  onClick={() => setModalPendientes(false)}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="modal-body">
              {cargandoModal ? (
                <div className="modal-loading">
                  <div className="spinner"></div>
                  <p>Cargando operaciones...</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="modal-table">
                    <thead>
                      <tr>
                        <th className="sortable" onClick={() => cambiarOrden("Area")}>
                          Etapa {orden.columna === "Area" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>

                        <th></th>
                        
                        <th className="sortable" onClick={() => cambiarOrden("Cliente")}>
                          Cliente {orden.columna === "Cliente" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>

                        <th className="sortable" onClick={() => cambiarOrden("Operacion")}>
                          Operacion {orden.columna === "Operacion" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>

                        <th className="sortable" onClick={() => cambiarOrden("Doc")}>
                          Doc {orden.columna === "Doc" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>

                        <th className="sortable" onClick={() => cambiarOrden("Fecha")}>
                          Fecha {orden.columna === "Fecha" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>

                        <th className="sortable" onClick={() => cambiarOrden("Hora_aprobH")}>
                          Prim. Aprob. {orden.columna === "Hora_aprobH" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>

                        <th className="sortable" onClick={() => cambiarOrden("Hora_aprobE")}>
                          Ult. Aprob. {orden.columna === "Hora_aprobE" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>

                        <th className="sortable" onClick={() => cambiarOrden("Monto")}>
                          Giro Real {orden.columna === "Monto" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>

                        <th className="sortable" onClick={() => cambiarOrden("Ejecutivo")}>
                          Ejecutivo {orden.columna === "Ejecutivo" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>
                        
                        <th className="sortable" onClick={() => cambiarOrden("Grupo")}>
                          Grupo {orden.columna === "Grupo" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>

                        <th className="sortable" onClick={() => cambiarOrden("Sucursal")}>
                          Sucursal {orden.columna === "Sucursal" && (orden.direccion === "asc" ? "▲" : "▼")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {listaPendientesOrdenada.map((operacion) => (
                        <tr key={operacion.N_Operacion} className={getClaseAlerta(operacion.Fecha, operacion.Hora_aprobE, operacion.Area)}>
                          <td>{operacion.Area}</td>
                          <td>{renderIcono(operacion.Icono)}</td>
                          <td>{operacion.Cliente}</td>
                          <td>{operacion.N_Operacion}</td>
                          <td>{operacion.TD}</td>
                          <td>{formatFecha(operacion.Fecha)}</td>
                          <td>{formatHora(operacion.Hora_aprobH)}</td>
                          <td>{formatHora(operacion.Hora_aprobE)}</td>
                          <td>{formatMonto(operacion.Monto)}</td>
                          <td>{operacion.Ejecutivo}</td>
                          <td>{operacion.GRUPO || '--'}</td>
                          <td>{operacion.SUCURSAL || '--'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PanelComercial;
